import { Field, Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  isValidAlphabetical,
  isValidBankAccountNumber,
  isValidBranchCode,
  isValidBranchName,
  isValidID,
  isValidRegistrationNumber,
  isValidRegistrationNumberTrust,
  validateBankAccountHolderNameTradeRoot,
  validateBusinessNameTradeRoot,
} from 'src/helpers/validation';
import {
  buyInstore as buyInstoreIcon,
  buyOnline as buyOnlineIcon,
  SelectedItem as SelectedItemCircle,
} from 'src/assets/images';
import { FloatingInput, RadioButton, RadioButtonGroup, ShowIf } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import {
  AcquiringChannelFlags,
  CustomerTypes,
  SalesforceContext,
  Steps,
  WalletTypes,
} from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import {
  AccountOwnerKind,
  AccountType,
  BankName,
  getIsBankAccountValid,
  IdType,
} from 'src/services/credit-vetting.service';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { BANK_ACCOUNT_HOLDER_TYPE, CONSUMER_TYPE, ID_TYPE } from 'src/services/salesforce.service';
import {
  ValidationFailModal,
  ValidationFailModalState,
  ValidationFailModalStateType,
} from './validation-fail-modal/validation-fail-modal';
import { WizardMessageNames } from 'src/containers/onboarding/state/wizard/wizard-message-names';
import { ReducerContext } from 'src/context/reducer-context';
import {
  businessRegistrationMask,
  trustRegistrationMask,
  idMask,
  returnValueIfTruthyElseEmptyString,
  stripWhitespace,
  spaceBy4Mask,
  passportMask,
  asylumMask,
  refugeeMask,
} from 'src/helpers/formatting';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { NavigateToNextStepMessage } from 'src/containers/onboarding/state/wizard/messages/navigate-to-next-step-message';
import { FloatingInputVersionTwo } from 'src/components';
import { Environment } from 'src/Environment';
import {
  onboardingContinueReferenceRoute,
  onboardingLetsGetStartedRoute,
} from 'src/routing/routes';
import useStateLocalPersist from 'src/hooks/use-state-local-persist';
import { Channel, CHANNEL_KEY } from '../../helpers/channel';
import { QueryConfigContext } from 'src/context/query-config-context';
import { deepEqual } from 'src/helpers/functional-helpers';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { ButtonParamType, DefaultButtonStyle } from 'src/components/button/button';
import ButtonGroup, { ButtonConfig, LayoutType } from 'src/components/button-group/button-group';
import { FormLabel, FormLayout, FormRowLayout } from 'src/components';
import useIsMobileDisplaySize from 'src/hooks/use-is-mobile-display-size';
import { checkAcquiringChannel } from 'src/helpers/devices';
import {
  ForeignNationalType,
  IdentityType,
  IdentityTypeDirectors,
  ListItem,
  isUserUnderAgeIdNumber,
} from 'src/features/common';
import UnderageModal from '../underage-modal/underage-modal';
import { getFormStatus, merger } from 'src/features/onboarding/helpers/fields-status';
import mergeWith from 'lodash.mergewith';
import { useKillApplication } from 'src/hooks/use-kill-application';
import { BankValidationService } from 'src/services/bank-validation.service';
import './your-details-banking.scss';

export default function YourDetailsBanking(props) {
  const {
    merchant,
    setMerchant,
    customerType,
    lead,
    getSettlementsMethod,
    revertApplicatonToSavedState,
    clearApplicationState,
    setBankingDetailsPassBav,
    getBankingDetailsPassBav,
    save,
    isVodacomBusinessChannel,
  } = useContext(SalesforceContext);
  const { dispatch, registerReducer } = useContext(ReducerContext);
  const { queryConfig } = useContext(QueryConfigContext);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);
  const [isUserUnderAge, setUserIsUnderAge] = useState(false);
  const { setApplicationKilled } = useKillApplication(Steps.YOUR_DETAILS, Steps.BANKING_DETAILS);
  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;
  const handleTechnicalError = useTechnicalErrorHandler();
  const isMobile = useIsMobileDisplaySize();
  const isDebitOrderDateEnabled = false;

  const isForeignNationalsEnabled = useMemo(
    (): boolean => !Environment.current?.disabledFeatures?.passport || false,
    [],
  );

  const inMiniApp = useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const NUMBER_OF_YEARS_BEFORE_CURRENT_YEAR_AUTO_COMPLETE = 100;

  const [currentDate] = useState(
    (() => {
      const date = new Date();

      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
      };
    })(),
  );

  const bankYearOpenedValues = useMemo(() => {
    const allYears: string[] = [];

    for (let i = 0; i < NUMBER_OF_YEARS_BEFORE_CURRENT_YEAR_AUTO_COMPLETE; i++) {
      allYears.push((currentDate.year - i).toString());
    }
    return allYears;
  }, [currentDate]);

  enum BusinessRegistrationType {
    'BUSINESS_REGISTRATION' = 'Business Registration',
    'RSA_ID' = 'RSA ID',
    IDENTIFICATION_NUMBER = 'IDENTIFICATION_NUMBER',
  }

  enum IdentificationType {
    SA_ID = IdentityType.RSA_ID,
    PASSPORT = ForeignNationalType.PASSPORT,
    ASYLUM_SEEKER = ForeignNationalType.ASYLUM_SEEKER,
    REFUGEE = ForeignNationalType.REFUGEE,
  }

  const IDENTIFICATION_TYPES: ListItem<IdentificationType>[] = [
    { value: IdentificationType.SA_ID, label: 'SA ID' },
    { value: IdentificationType.PASSPORT, label: 'Passport' },
    { value: IdentificationType.ASYLUM_SEEKER, label: 'Asylum seeker' },
    { value: IdentificationType.REFUGEE, label: 'Refugee' },
  ];

  const VALIDATE_MAX_TRIES = 3; // if you change this, change the line below too to match
  const VALIDATE_MAX_TRIES_WORD = 'three';
  const PAGE_NAME = 'banking-details';

  const [validateTries, setValidateTries] = useStateLocalPersist(
    'banking-details-validate-tries',
    0,
  );
  const [validationFailModalProps, setValidationFailModalProps] =
    useState<ValidationFailModalState>({ state: ValidationFailModalStateType.HIDDEN });
  const [hasOrderedRentOnlineDevice, setHasOrderedRentOnlineDevice] = useState(false);

  const getHasOrderedRentOnlineDevice = (deliveries) =>
    Object.values(deliveries).some(
      (delivery) =>
        // @ts-ignore
        delivery.acquiring_channel === AcquiringChannelFlags.rentOnlineFicaOnline && // @ts-ignore
        delivery.number_of_devices > 0,
    );

  useEffect(() => {
    if (merchant?.opportunity?.deliveries) {
      const rentOnlineDeviceOrdered = getHasOrderedRentOnlineDevice(
        merchant?.opportunity?.deliveries,
      );
      // work around for when the user removes the max using the basket
      if (!rentOnlineDeviceOrdered && !!merchant?.bank_account?.debit_order_date) {
        setMerchant({
          ...merchant,
          bank_account: {
            ...merchant.bank_account,
            debit_order_date: null,
          },
        });
      }
      setHasOrderedRentOnlineDevice(rentOnlineDeviceOrdered);
    }
  }, [merchant]);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.bankingPageLoadFormStartCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  useEffect(() => {
    const errors = getFormErrors(getBankDetails());
    props.toggleNext(!isFormHasErrors(errors));
  }, []);

  const getIsAccountDetailsValid = async (accountDetails) => {
    const bankAccountType = (function () {
      switch (accountDetails.accountType) {
        case BankAccountType.Savings:
          return AccountType.Savings;
        case BankAccountType.CurrentCheque:
          return AccountType.Current;
      }
    })();

    const ownerDetails =
      accountDetails.bankAccountHolderType === bankInfo.accountHolderType.PERSONAL ||
      (accountDetails.bankAccountHolderType === bankInfo.accountHolderType.BUSINESS &&
        accountDetails.businessRegistrationType === BusinessRegistrationType.RSA_ID) ||
      (accountDetails.businessRegistrationType === BusinessRegistrationType.IDENTIFICATION_NUMBER &&
        accountDetails.identificationType?.value === IdentificationType.SA_ID)
        ? {
            kind: AccountOwnerKind.Individual,
            idNumber: stripWhitespace(accountDetails.bankAccountHolderIdNumber),
            idType: IdType.SouthAfricanID,
            surname: accountDetails.bankAccountHolder,
          }
        : {
            kind: AccountOwnerKind.Company,
            companyName: accountDetails.businessName,
            idType: IdType.SouthAfricanBusiness,
            companyRegistrationNumber: stripWhitespace(accountDetails.businessRegistrationNumber),
          };

    if (
      accountDetails.bankAccountHolderType === bankInfo.accountHolderType.BUSINESS &&
      accountDetails.businessRegistrationType === BusinessRegistrationType.IDENTIFICATION_NUMBER &&
      accountDetails.identificationType.value === IdentificationType.PASSPORT
    ) {
      ownerDetails.idType = IdType.ForeignPassport;
    }

    const isAccountDetailsValid = await getIsBankAccountValid(
      accountDetails.bankAccountNumber, // @ts-ignore
      bankAccountType,
      accountDetails.bank?.creditVettingValue,
      accountDetails.bank?.universalBranchCode,
      ownerDetails,
      null, // @ts-ignore
      (errorMessage, errorDetails) => handleTechnicalError(errorMessage, null, null, errorDetails),
    );

    return isAccountDetailsValid;
  };

  const doesCDVCheckPass = async (accountDetails) => {
    const bankBranchCode = accountDetails.branchCode || accountDetails.bank?.universalBranchCode;
    const bankAccountType = (function () {
      switch (accountDetails.accountType) {
        case BankAccountType.Savings:
          return AccountType.Savings;
        case BankAccountType.CurrentCheque:
          return AccountType.Current;
        default:
          throw new Error(
            `Your Details Banking: CDV: Unknown bank account type: ${accountDetails.accountType}`,
          );
      }
    })();

    if (!bankBranchCode || !accountDetails.bankAccountNumber) {
      return false;
    }

    return await BankValidationService.cdvCheck(
      bankBranchCode,
      accountDetails.bankAccountNumber,
      bankAccountType,
      new AbortController(),
    );
  };

  const setupMaxValidateTriesExceededState = () => {
    const onRestartButtonClick = () => {
      TealiumEvents.onOnboardingbuttonClick('restart application', customerType, PAGE_NAME);
      const isVodacomBusinessChannel = queryConfig[CHANNEL_KEY] === Channel.VODACOM_BUSINESS;

      if (isVodacomBusinessChannel) {
        props.history.push(
          `${onboardingLetsGetStartedRoute}?${CHANNEL_KEY}=${Channel.VODACOM_BUSINESS}`,
        );
      } else {
        props.history.push(onboardingContinueReferenceRoute);
      }

      clearApplicationState();
    };

    setValidationFailModalProps({
      state: ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED,
      maxNumberOfTriesWord: VALIDATE_MAX_TRIES_WORD,
      onRestartApplication: onRestartButtonClick,
    });
  };

  useEffect(() => {
    if (isCreditVettingEnabled && validateTries >= VALIDATE_MAX_TRIES) {
      if (getBankingDetailsPassBav()) {
        setBankingDetailsPassBav(false);
        setValidateTries(0);
      } else {
        revertApplicatonToSavedState();
        setupMaxValidateTriesExceededState();
      }
    }
  }, []);

  useEffect(() => {
    if (isCreditVettingEnabled) {
      const unregister = registerReducer({
        apply: (message: NavigateToNextStepMessage) => {
          switch (message.name) {
            case WizardMessageNames.NAVIGATE_TO_NEXT_STEP:
              if (!isValidationInProgress) {
                // replace the default handling of the wizard next button click
                setIsValidationInProgress(true);
                return {
                  discardMessage: true,
                };
              }
              break;
          }
          return {};
        },
        applicableMessageNames: [WizardMessageNames.NAVIGATE_TO_NEXT_STEP],
      });

      if (isValidationInProgress) {
        setValidateTries((validateTriesCurrentValue) => {
          const validateTriesNewValue = validateTriesCurrentValue + 1;

          (async () => {
            dispatch(WizardMessageNames.SHOW_PAGE_LOADER);

            const accountDetails = getBankDetails();
            let isAccountDetailsValid = false;

            if (
              // @ts-ignore
              (accountDetails.bankAccountHolderType === BANK_ACCOUNT_HOLDER_TYPE.BUSINESS && // @ts-ignore
                (accountDetails.identificationType?.value === IdentificationType.ASYLUM_SEEKER || // @ts-ignore
                  accountDetails.identificationType?.value === IdentificationType.REFUGEE)) || // @ts-ignore
              (accountDetails.bankAccountHolderType === BANK_ACCOUNT_HOLDER_TYPE.PERSONAL && // @ts-ignore
                accountDetails.accountHolderIDType !== IdentityTypeDirectors.RSA_ID && // @ts-ignore
                accountDetails.accountHolderIDType !== IdentityTypeDirectors.PASSPORT)
            ) {
              isAccountDetailsValid = await doesCDVCheckPass(accountDetails);
            } else {
              isAccountDetailsValid = (await getIsAccountDetailsValid(accountDetails)) || false;
            }

            TealiumEvents.bankingClickNext(
              customerType, // @ts-ignore
              accountDetails.bankAccountHolderType, // @ts-ignore
              accountDetails.debitOrderDate,
              isAccountDetailsValid ? 'validation passed' : 'validation unsuccessful',
              merchant.isPendingApp,
            );

            if (isAccountDetailsValid) {
              setBankingDetailsPassBav(true);
              setIsValidationInProgress(false);
              dispatch(WizardMessageNames.NAVIGATE_TO_NEXT_STEP);
            } else {
              setBankingDetailsPassBav(false);
              await save(Steps.YOUR_DETAILS, Steps.BANKING_DETAILS, false);
              setIsValidationInProgress(false);

              const onEditButtonClick = () => {
                TealiumEvents.onOnboardingbuttonClick('edit bank details', customerType, PAGE_NAME);
                setValidationFailModalProps({
                  state: ValidationFailModalStateType.HIDDEN,
                });
              };

              switch (validateTriesNewValue) {
                case VALIDATE_MAX_TRIES:
                  revertApplicatonToSavedState();
                  setupMaxValidateTriesExceededState();
                  break;
                case VALIDATE_MAX_TRIES - 1:
                  TealiumEvents.onHandledErrorEventOnboarding(
                    'banking details verification failed - verification 2',
                    PAGE_NAME,
                  );
                  setValidationFailModalProps({
                    state: ValidationFailModalStateType.FAILED_ONE_TRY_LEFT,
                    onEditBankDetailsButtonClick: onEditButtonClick,
                  });
                  break;

                default:
                  TealiumEvents.onHandledErrorEventOnboarding(
                    'banking details verification failed',
                    PAGE_NAME,
                  );
                  setValidationFailModalProps({
                    state: ValidationFailModalStateType.FAILED,
                    onEditBankDetailsButtonClick: onEditButtonClick,
                  });
                  break;
              }

              dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
            }
          })();

          return validateTriesNewValue;
        });
      } else {
        dispatch(WizardMessageNames.HIDE_PAGE_LOADER);
      }

      return () => {
        unregister();
      };
    }
  }, [isValidationInProgress]);

  const BankAccountType = {
    CurrentCheque: 'Current Cheque Account',
    Savings: 'Savings',
  };

  const DEFAULT_UNIVERSAL_BRANCH_CODE_NAME = 'Universal Bank Codes';

  // TODO: Move this banklist to one file (it is a constant)
  const bankInfo = {
    bankList: [
      {
        label: 'First National Bank',
        salesforceValue: 'FIRST NATIONAL BANK',
        creditVettingValue: BankName.firstNationalBank,
        universalBranchCode: '254005',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Nedbank',
        salesforceValue: 'NEDBANK',
        creditVettingValue: BankName.nedbank,
        universalBranchCode: '198765',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Standard Bank SA',
        salesforceValue: 'STANDARD BANK SA',
        creditVettingValue: BankName.standardBankSa,
        universalBranchCode: '051001',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Standard Bank',
        salesforceValue: 'STANDARD BANK',
        creditVettingValue: BankName.standardBank,
        universalBranchCode: '051001',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'ABSA Bank',
        salesforceValue: 'ABSA BANK',
        creditVettingValue: BankName.absaBank,
        universalBranchCode: '632005',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Capitec Bank',
        salesforceValue: 'CAPITEC BANK',
        creditVettingValue: BankName.capitecBank,
        universalBranchCode: '470010',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Investec',
        salesforceValue: 'INVESTEC',
        creditVettingValue: BankName.investec,
        universalBranchCode: '580105',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Discovery Bank',
        salesforceValue: 'DISCOVERY BANK',
        creditVettingValue: BankName.discoveryBank,
        universalBranchCode: '679000',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Sasfin',
        salesforceValue: 'SASFIN',
        creditVettingValue: BankName.SASFIN,
        universalBranchCode: '683000',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'African Bank',
        salesforceValue: 'AFRICAN BANK',
        creditVettingValue: BankName.africanBank,
        universalBranchCode: '430000',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'ABSA Bank Limited',
        salesforceValue: 'ABSA BANK LIMITED',
        creditVettingValue: BankName.absaBankLimited,
        universalBranchCode: '632005',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'SASFIN Bank Limited',
        salesforceValue: 'SASFIN BANK LIMITED',
        creditVettingValue: BankName.sasfinBankLimited,
        universalBranchCode: '683000',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Investec Bank',
        salesforceValue: 'INVESTEC BANK',
        creditVettingValue: BankName.investecBank,
        universalBranchCode: '580105',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Tyme Bank Limited',
        salesforceValue: 'TYME BANK LIMITED',
        creditVettingValue: BankName.tymeBankLimited,
        universalBranchCode: '678910',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Grindrod Bank',
        salesforceValue: 'GRINDROD BANK',
        creditVettingValue: BankName.grindrodBank,
        universalBranchCode: '584000',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'Albaraka Bank',
        salesforceValue: 'ALBARAKA BANK',
        creditVettingValue: BankName.albarakaBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Bank Zero',
        salesforceValue: 'BANK ZERO',
        creditVettingValue: BankName.bankZero,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Bidvest Bank',
        salesforceValue: 'BIDVEST BANK',
        creditVettingValue: BankName.bidvestBank,
        universalBranchCode: '462005',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'BNP Paribas',
        salesforceValue: 'BNP PARIBAS',
        creditVettingValue: BankName.bnpParibas,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'CitiBank',
        salesforceValue: 'CITIBANK',
        creditVettingValue: BankName.citiBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'FinBond Mutual Bank',
        salesforceValue: 'FINBOND MUTUAL BANK',
        creditVettingValue: BankName.finBond,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'FinBond EPE',
        salesforceValue: 'FINBOND EPE',
        creditVettingValue: BankName.finBondEPE,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'GROBANK LTD',
        salesforceValue: 'GROBANK LTD',
        creditVettingValue: BankName.groBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'HBZ Bank',
        salesforceValue: 'HBZ BANK',
        creditVettingValue: BankName.hbzBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'HSBC Bank',
        salesforceValue: 'HSBC BANK',
        creditVettingValue: BankName.hsbcBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Habib Overseas Bank',
        salesforceValue: 'HABIB OVERSEAS BANK',
        creditVettingValue: BankName.habibBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Ithala',
        salesforceValue: 'ITHALA',
        creditVettingValue: BankName.ithala,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'JP Morgan Chase',
        salesforceValue: 'JP MORGAN CHASE',
        creditVettingValue: BankName.jpMorgan,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Lesotho Post Bank',
        salesforceValue: 'LESOTHO POST BANK',
        creditVettingValue: BankName.lesothoPostBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Olympus Mobile',
        salesforceValue: 'OLYMPUS MOBILE',
        creditVettingValue: BankName.olympusMobile,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Peoples Bank Ltd Inc NBS',
        salesforceValue: 'PEOPLES BANK LTD INC NBS',
        creditVettingValue: BankName.peoplesBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'PostBank',
        salesforceValue: 'POSTBANK',
        creditVettingValue: BankName.postBank,
        universalBranchCode: '460005',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
      },
      {
        label: 'S.A. Reserve Bank',
        salesforceValue: 'S.A. RESERVE BANK',
        creditVettingValue: BankName.saReserve,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Societe Generale',
        salesforceValue: 'SOCIETE GENERALE',
        creditVettingValue: BankName.societeGenerale,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'Standard Chartered Bank',
        salesforceValue: 'STANDARD CHARTERED BANK',
        creditVettingValue: BankName.standardChartered,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'State Bank of India',
        salesforceValue: 'STATE BANK OF INDIA',
        creditVettingValue: BankName.stateBankIndia,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'UBank Limited',
        salesforceValue: 'UBANK LIMITED',
        creditVettingValue: BankName.uBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
      {
        label: 'VBS Mutual Bank',
        salesforceValue: 'VBS MUTUAL BANK',
        creditVettingValue: BankName.vbsMutualBank,
        universalBranchCode: '0',
        universalBranchName: DEFAULT_UNIVERSAL_BRANCH_CODE_NAME,
        allowUserToOverrideBranch: true,
      },
    ],
    debitOrderValues: [
      'First day of month',
      'Last day of the month',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
      '21',
      '22',
      '23',
      '24',
      '25',
      '26',
      '27',
      '28',
      '29',
      '30',
    ],
    accountTypes: [BankAccountType.CurrentCheque, BankAccountType.Savings],
    accountHolderType: {
      PERSONAL: BANK_ACCOUNT_HOLDER_TYPE.PERSONAL,
      BUSINESS: BANK_ACCOUNT_HOLDER_TYPE.BUSINESS,
    },
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  };

  const updateWalletType = (walletType) => {
    setMerchant((merchant) => ({
      ...merchant,
      account: {
        ...merchant.account,
        settlements_method: walletType,
      },
    }));
  };

  const MAX_BANK_ACCOUNT_NUMBER_LENGTH = 16;

  const matchesIgnoreCase = (element, unformattedBankName) =>
    !!unformattedBankName ? element.toUpperCase() === unformattedBankName.toUpperCase() : false;

  const getBank = (unformattedBankName) =>
    bankInfo.bankList.find((element) =>
      matchesIgnoreCase(element.salesforceValue, unformattedBankName),
    );

  const setBankDetailsCreditVettingDisabled = (newMerchantDetails) => {
    setMerchant({
      ...merchant,
      bank_account: {
        ...merchant.bank_account,
        branch_name: newMerchantDetails.branchName,
        branch_code: newMerchantDetails.branchCode,
        bank_name: newMerchantDetails.bank.salesforceValue,
        bank_account_number: newMerchantDetails.bankAccountNumber,
        bank_account_holder: newMerchantDetails.bankAccountHolder,
        account_type: newMerchantDetails.accountType,
      },
    });
  };

  const setBankDetailsCreditVettingEnabled = (newMerchantDetails) => {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { debit_order_date, ...bankDetailsNew } = {
      ...merchant.bank_account,
      bank_name: newMerchantDetails.bank.salesforceValue,
      bank_account_number: newMerchantDetails.bankAccountNumber,
      account_type: newMerchantDetails.accountType,
      bank_account_holder_type: newMerchantDetails.bankAccountHolderType,
      bank_account_open_months: newMerchantDetails.bankAccountOpenSinceMonth.index.toString(),
      bank_account_open_years: newMerchantDetails.bankAccountOpenSinceYear,
    };
    const accountDetailsNew = { ...merchant.account };

    if (newMerchantDetails.bank.allowUserToOverrideBranch) {
      bankDetailsNew.branch_name = newMerchantDetails.branchName;
      bankDetailsNew.branch_code = newMerchantDetails.branchCode;
    } else {
      bankDetailsNew.branch_name = newMerchantDetails.bank.universalBranchName;
      bankDetailsNew.branch_code = newMerchantDetails.bank.universalBranchCode;
    }

    switch (newMerchantDetails.bankAccountHolderType) {
      case bankInfo.accountHolderType.PERSONAL:
        bankDetailsNew.bank_account_holder_personal = newMerchantDetails.bankAccountHolder;
        bankDetailsNew.bank_account_holder_id_number = newMerchantDetails.bankAccountHolderIdNumber;
        break;
      case bankInfo.accountHolderType.BUSINESS:
        bankDetailsNew.bank_account_holder_business = newMerchantDetails.businessName;
        bankDetailsNew.business_registration_type = newMerchantDetails.businessRegistrationType;

        switch (newMerchantDetails.businessRegistrationType) {
          case BusinessRegistrationType.BUSINESS_REGISTRATION:
            bankDetailsNew.business_registration_number =
              newMerchantDetails.businessRegistrationNumber;
            break;

          case BusinessRegistrationType.RSA_ID:
            bankDetailsNew.bank_account_holder_id_number =
              newMerchantDetails.bankAccountHolderIdNumber;
            break;

          case BusinessRegistrationType.IDENTIFICATION_NUMBER:
            bankDetailsNew.bank_account_holder_id_number =
              newMerchantDetails.bankAccountHolderIdNumber;
            bankDetailsNew.identification_type = newMerchantDetails.identificationType?.value;
            break;
        }
        break;
    }

    if (newMerchantDetails.rentOnlineDeviceOrdered) {
      bankDetailsNew.debit_order_date = newMerchantDetails.debitOrderDate;
    }

    if (merchant?.account?.sov_requested && bankDetailsNew.rentOnlineDeviceOrdered) {
      accountDetailsNew.settlements_method = WalletTypes.BANK_ACCOUNT;
    }

    setMerchant({
      ...merchant,
      bank_account: bankDetailsNew,
      account: accountDetailsNew,
    });
  };

  const getDefaultBankAccountHolderPersonal = () => {
    const firstName = merchant?.contact?.first_names || '';
    const surname = merchant?.contact?.surname || '';
    return `${firstName} ${surname}`;
  };

  const getAccountHolderType = () => {
    const hasBusinessCompanyType =
      merchant.account.business_type !== BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop;
    const accountHolderTypeCurrent = merchant?.bank_account?.bank_account_holder_type;
    return (
      accountHolderTypeCurrent
        ? accountHolderTypeCurrent === bankInfo.accountHolderType.BUSINESS
        : hasBusinessCompanyType
    )
      ? bankInfo.accountHolderType.BUSINESS
      : bankInfo.accountHolderType.PERSONAL;
  };

  const getIdentificationTypeFromContact = (): IdentificationType | undefined => {
    if (merchant?.bank_account?.identification_type) {
      return merchant?.bank_account?.identification_type;
    }

    switch (merchant.contact?.id_type) {
      case IdentityType.RSA_ID:
      case 'id':
        return IdentificationType.SA_ID;
      case ForeignNationalType.PASSPORT:
        return IdentificationType.PASSPORT;
      case ForeignNationalType.REFUGEE:
        return IdentificationType.REFUGEE;
      case ForeignNationalType.ASYLUM_SEEKER:
        return IdentificationType.ASYLUM_SEEKER;
      default:
        return undefined;
    }
  };

  const getBankDetailsCreditVettingDisabled = () => {
    return {
      bank: getBank(merchant?.bank_account?.bank_name),
      bankAccountNumber: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.bank_account_number,
      ),
      accountType: returnValueIfTruthyElseEmptyString(merchant?.bank_account?.account_type),
      bankAccountHolder: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.bank_account_holder,
      ),
      branchCode: returnValueIfTruthyElseEmptyString(merchant?.bank_account?.branch_code),
      branchName: returnValueIfTruthyElseEmptyString(merchant?.bank_account?.branch_name),
    };
  };

  const getBankDetailsCreditVettingEnabled = () => {
    const bank = getBank(merchant?.bank_account?.bank_name);

    const bankAccountOpenMonths = (() => {
      const currentValue = merchant?.bank_account?.bank_account_open_months;

      if (currentValue) {
        const numberOfMonths = parseInt(currentValue);

        return {
          index: numberOfMonths,
          label: bankInfo.months[numberOfMonths - 1],
        };
      }

      return null;
    })();

    const isPartnership =
      merchant?.account?.business_type === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.partnership;
    const isBankAccountHolderIdNumberSet = !!merchant?.bank_account?.bank_account_holder_id_number;

    const getFirstAccountIdNumber = () => {
      const accountIdNumbers = merchant.account.directors
        .filter((director) => director.id_type === ID_TYPE.SA_ID)
        .map((director) => director.directors_id_number);

      return accountIdNumbers.length > 0 ? accountIdNumbers[0] : null;
    };

    const bankDetails = {
      bank: bank,
      bankAccountHolder:
        merchant?.bank_account?.bank_account_holder_personal ||
        getDefaultBankAccountHolderPersonal(),
      bankAccountNumber: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.bank_account_number,
      ),
      accountType: returnValueIfTruthyElseEmptyString(merchant?.bank_account?.account_type),
      bankAccountHolderType: getAccountHolderType(),
      bankAccountHolderIdNumber:
        isPartnership && !isBankAccountHolderIdNumberSet
          ? returnValueIfTruthyElseEmptyString(getFirstAccountIdNumber())
          : returnValueIfTruthyElseEmptyString(
              merchant?.bank_account?.bank_account_holder_id_number || merchant?.contact?.id_number,
            ),
      accountHolderIDType: merchant.contact?.id_type || IdentityType.RSA_ID,
      businessName: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.bank_account_holder_business || merchant?.account?.business_name,
      ),
      businessRegistrationNumber: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.business_registration_number ||
          merchant?.account?.registration_number,
      ),
      businessRegistrationType: returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.business_registration_type,
      ),
      bankAccountOpenSinceYear:
        returnValueIfTruthyElseEmptyString(merchant?.bank_account?.bank_account_open_years) + '',
      bankAccountOpenSinceMonth: returnValueIfTruthyElseEmptyString(bankAccountOpenMonths),
      identificationType: IDENTIFICATION_TYPES.find(
        (type) => type.value === getIdentificationTypeFromContact(),
      ),
    };

    // @ts-ignore
    if (!bankDetails.businessRegistrationType) {
      bankDetails.businessRegistrationType = !!bankDetails.businessRegistrationNumber
        ? BusinessRegistrationType.BUSINESS_REGISTRATION
        : isForeignNationalsEnabled
        ? BusinessRegistrationType.IDENTIFICATION_NUMBER
        : BusinessRegistrationType.RSA_ID;
    }

    if (bank?.allowUserToOverrideBranch) {
      // @ts-ignore
      bankDetails.branchCode = returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.branch_code || bank?.universalBranchCode,
      ); // @ts-ignore
      bankDetails.branchName = returnValueIfTruthyElseEmptyString(
        merchant?.bank_account?.branch_name || bank?.universalBranchName,
      );
    } else {
      // @ts-ignore
      bankDetails.branchCode = returnValueIfTruthyElseEmptyString(bank?.universalBranchCode); // @ts-ignore
      bankDetails.branchName = returnValueIfTruthyElseEmptyString(bank?.universalBranchName);
    }

    // @ts-ignore
    bankDetails.rentOnlineDeviceOrdered = hasOrderedRentOnlineDevice;
    // @ts-ignore
    if (bankDetails.rentOnlineDeviceOrdered) {
      // @ts-ignore
      bankDetails.debitOrderDate = isDebitOrderDateEnabled
        ? returnValueIfTruthyElseEmptyString(merchant?.bank_account?.debit_order_date)
        : null;
    }

    return bankDetails;
  };

  const setBankDetails = (newMerchantDetails) =>
    isCreditVettingEnabled
      ? setBankDetailsCreditVettingEnabled(newMerchantDetails)
      : setBankDetailsCreditVettingDisabled(newMerchantDetails);

  const getBankDetails = () =>
    isCreditVettingEnabled
      ? getBankDetailsCreditVettingEnabled()
      : getBankDetailsCreditVettingDisabled();

  const getInitialTouchedCreditVettingEnabled = () => {
    return {
      bankAccountHolder: true,
      businessName: true,
    };
  };

  const getInitialTouched = () =>
    isCreditVettingEnabled ? getInitialTouchedCreditVettingEnabled() : {};

  const businessRegistrationButton: ButtonConfig = {
    automationId:
      'Onboarding_Create_Account_Your_Details_Banking_Button_Business_Registration_Type_Business',
    label: 'Business registration',
    buttonStyle: DefaultButtonStyle()
      .hasSecondaryAction()
      .isLargeSize()
      .hasDarkMode()
      .isResponsive()
      .build(),
    buttonParams: {
      type: ButtonParamType.RadioButton,
      value: BusinessRegistrationType.BUSINESS_REGISTRATION,
    },
  };

  const southAfricanIDButton: ButtonConfig = {
    automationId:
      'Onboarding_Create_Account_Your_Details_Banking_Button_Business_Registration_Type_RSA_ID',
    label: 'RSA ID',
    buttonStyle: DefaultButtonStyle()
      .hasSecondaryAction()
      .isLargeSize()
      .hasDarkMode()
      .isResponsive()
      .build(),
    buttonParams: {
      type: ButtonParamType.RadioButton,
      value: BusinessRegistrationType.RSA_ID,
    },
  };

  const identificationButton: ButtonConfig = {
    automationId:
      'Onboarding_Create_Account_Your_Details_Banking_Button_Business_Registration_Type_Identification_Number',
    label: 'Identification number',
    buttonStyle: DefaultButtonStyle()
      .hasSecondaryAction()
      .isLargeSize()
      .hasDarkMode()
      .isResponsive()
      .build(),
    buttonParams: {
      type: ButtonParamType.RadioButton,
      value: BusinessRegistrationType.IDENTIFICATION_NUMBER,
    },
  };

  const getButtons = (): ButtonConfig[] => {
    if (isForeignNationalsEnabled) {
      return [businessRegistrationButton, identificationButton];
    }

    return [businessRegistrationButton, southAfricanIDButton];
  };

  const initialStatus = useMemo(() => {
    const defaultStatus = {
      initialDisabled: {
        bank: false,
        bankAccountHolder: false,
        bankAccountNumber: false,
        accountType: false,
        bankAccountHolderType: false,
        bankAccountHolderIdNumber: false,
        businessName: false,
        businessRegistrationNumber: false,
        businessRegistrationType: false,
        bankAccountOpenSinceYear: false,
        bankAccountOpenSinceMonth: false,
        debitOrderDate: false,
        branchName: false,
        branchCode: false,
      },
    };

    if (merchant.isPendingApp && !merchant?.pageUpdates) {
      throw new Error('Your details banking: in pending app but no page updates available');
    }

    if (!merchant.isPendingApp) {
      return defaultStatus;
    }

    const allFields = getBankDetails();
    const pendingAppStatus = getFormStatus(merchant.pageUpdates, allFields, Steps.BANKING_DETAILS);

    if (isVodacomBusinessChannel) {
      return mergeWith(pendingAppStatus, defaultStatus, merger);
    }

    return pendingAppStatus;
  }, [merchant?.isPendingApp, merchant?.pageUpdates]);

  const getIdPlaceholder = (identificationType?: IdentificationType) => {
    switch (identificationType) {
      case IdentificationType.PASSPORT:
        return 'Account holder passport number';
      case IdentificationType.REFUGEE:
      case IdentificationType.ASYLUM_SEEKER:
        return 'Account holder document ID';
      case IdentificationType.SA_ID:
      default:
        return 'Account holder ID number';
    }
  };

  const getIdMaskPersonal = () => {
    if (merchant.contact.id_type === IdentificationType.SA_ID) return idMask;

    switch (merchant.contact.id_type) {
      case IdentificationType.PASSPORT:
        return passportMask;
      case IdentificationType.ASYLUM_SEEKER:
        return asylumMask;
      case IdentificationType.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  const getIdMaskBusiness = (values) => {
    if (values.identificationType?.value === IdentificationType.SA_ID) return idMask;

    switch (values.identificationType?.value) {
      case IdentificationType.PASSPORT:
        return passportMask;
      case IdentificationType.ASYLUM_SEEKER:
        return asylumMask;
      case IdentificationType.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  return (
    <div className="YourDetailsBanking" data-testid="YourDetailsBanking">
      <ValidationFailModal {...validationFailModalProps} />
      <ShowIf condition={!!merchant?.account?.sov_requested && !hasOrderedRentOnlineDevice}>
        <>
          <h4 className="page-title heading-four has-text-centered has-padding-bottom-4 has-mobile-padding-bottom-6 has-padding-x-1 has-margin-x-4">
            Which account would you like to use?
          </h4>
          <div className="option-selection is-mobile is-center">
            <div
              automationid={`Onboarding_Button_BusinessType`}
              className="card-padding"
              onClick={() => updateWalletType(WalletTypes.SOV)}
            >
              <div className="icon-wrapper">
                <div className="Icon_Box">
                  <div
                    automationid={`Onboarding_Button_BusinessTypeName`}
                    className={`has-padding-y-7 has-padding-x-3 square-selectable-item ${
                      getSettlementsMethod() === WalletTypes.SOV ? 'active' : ''
                    }`}
                  >
                    <div
                      automationid={`Onboarding_Button_BusinessTypeIcon`}
                      className={`middle-icon`}
                    >
                      <img className="journeyType" src={buyInstoreIcon} />
                    </div>
                    Pay into my Biz Wallet
                  </div>
                </div>
                <div className="selectionBox">
                  {getSettlementsMethod() === WalletTypes.SOV ? (
                    <img src={SelectedItemCircle} />
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </div>
            <div
              automationid={`Onboarding_Button_BusinessType`}
              className="card-padding"
              onClick={() => updateWalletType(WalletTypes.BANK_ACCOUNT)}
            >
              <div className="icon-wrapper">
                <div className="Icon_Box">
                  <div
                    automationid={`Onboarding_Button_BusinessTypeName`}
                    className={`has-padding-y-7 has-padding-x-3 square-selectable-item ${
                      getSettlementsMethod() === WalletTypes.BANK_ACCOUNT ? 'active' : ''
                    }`}
                  >
                    <div
                      automationid={`Onboarding_Button_BusinessTypeIcon`}
                      className={`middle-icon`}
                    >
                      <img className="journeyType" src={buyOnlineIcon} />
                    </div>
                    Pay into my bank account
                  </div>
                </div>
                <div className="selectionBox">
                  {getSettlementsMethod() === WalletTypes.BANK_ACCOUNT ? (
                    <img src={SelectedItemCircle} />
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      </ShowIf>
      <ShowIf condition={getSettlementsMethod() === WalletTypes.BANK_ACCOUNT}>
        <Formik
          initialValues={getBankDetails()}
          initialStatus={initialStatus}
          initialTouched={getInitialTouched()}
          validate={validateForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
          onSubmit={() => {}}
        >
          {({ values, setFieldValue }) => {
            // @ts-ignore
            const yourBankingDetails = () => (
              <>
                <p className="page-subtitle has-padding-bottom-2 has-mobile-padding-bottom-3">
                  {(customerType === CustomerTypes.normal &&
                    lead.consumerType === CONSUMER_TYPE.owner) ||
                  customerType === CustomerTypes.pos
                    ? 'The banking details below will be used to deduct your monthly installments and to pay your daily settlements to you.'
                    : "The customer's banking details for us to pay daily settlements."}
                </p>
                <Form className="text-left">
                  <UnderageModal
                    show={isUserUnderAge}
                    onExitApplication={() => {
                      setApplicationKilled(true);
                    }}
                    onClose={() => {
                      setUserIsUnderAge(false);
                    }}
                  />
                  <ShowIf condition={isCreditVettingEnabled}>
                    <RadioButtonGroup
                      className="bank-account-type-radio-buttons"
                      automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type"
                    >
                      <Field
                        type="radio"
                        name="bankAccountHolderType"
                        value={bankInfo.accountHolderType.PERSONAL}
                        label={isMobile ? 'Personal account' : 'Personal bank account'}
                        component={RadioButton}
                        automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Personal"
                      />
                      <Field
                        type="radio"
                        name="bankAccountHolderType"
                        value={bankInfo.accountHolderType.BUSINESS}
                        label={isMobile ? 'Business account' : 'Business bank account'}
                        component={RadioButton}
                        automationId="Onboarding_Create_Account_Your_Details_Radio_Button_Group_Account_Type_Value_Business"
                      />
                    </RadioButtonGroup>

                    <ShowIf
                      condition={
                        /* @ts-ignore */
                        values.bankAccountHolderType === bankInfo.accountHolderType.BUSINESS &&
                        (merchant?.account?.business_type ===
                          BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop ||
                          merchant?.account?.business_type ===
                            BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.partnership)
                      }
                    >
                      <FormLabel
                        label="Account registration type"
                        className="business-registration-type-options"
                      >
                        <Field
                          name="businessRegistrationType"
                          component={ButtonGroup}
                          buttons={getButtons()}
                          layoutType={LayoutType.FULL_WIDTH}
                        />
                      </FormLabel>
                    </ShowIf>
                  </ShowIf>

                  <ShowIf condition={!isCreditVettingEnabled}>
                    <Field
                      name="bankAccountHolder"
                      type="text"
                      placeholder="Account holder name"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="dark top-field"
                      automationid="Onboarding_Create_Account_Your_Details_Field_Bank_Account_Holder"
                    />
                  </ShowIf>

                  <ShowIf condition={isCreditVettingEnabled}>
                    <ShowIf
                      condition={
                        /* @ts-ignore */
                        values.bankAccountHolderType === bankInfo.accountHolderType.PERSONAL
                      }
                    >
                      <Field
                        name="bankAccountHolder"
                        type="text"
                        placeholder="Account holder full name"
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        fieldType="standard"
                        className="dark top-field"
                        automationid="Onboarding_Create_Account_Your_Details_Field_Bank_Account_Holder"
                      />
                      <Field
                        name="bankAccountHolderIdNumber"
                        type={
                          merchant.contact.id_type === IdentificationType.SA_ID ? 'tel' : 'text'
                        }
                        mask={getIdMaskPersonal()}
                        placeholder="Account holder ID number"
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        fieldType="masked"
                        guide={!inMiniApp && merchant.contact.id_type === IdentificationType.SA_ID}
                        className="dark"
                        automationid="Onboarding_Create_Account_Your_Details_Field_Bank_Account_Holder_Id_Number"
                      />
                    </ShowIf>

                    <ShowIf
                      condition={
                        /* @ts-ignore */
                        values.bankAccountHolderType === bankInfo.accountHolderType.BUSINESS
                      }
                    >
                      <Field
                        name="businessName"
                        type="text"
                        placeholder="Business name"
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        fieldType="standard"
                        className="dark top-field"
                        automationid="Onboarding_Create_Account_Your_Details_Field_Business_Name"
                      />

                      <ShowIf
                        condition={
                          /* @ts-ignore */
                          values.businessRegistrationType ===
                          BusinessRegistrationType.BUSINESS_REGISTRATION
                        }
                      >
                        <Field
                          name="businessRegistrationNumber"
                          mask={
                            merchant?.account?.business_type ===
                            BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.trust
                              ? trustRegistrationMask
                              : businessRegistrationMask
                          }
                          type="text"
                          placeholder="Business registration number"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          fieldType="masked"
                          guide={!inMiniApp}
                          className="dark"
                          automationid="Onboarding_Create_Account_Your_Details_Field_Business_Registration_Number"
                        />
                      </ShowIf>

                      <ShowIf
                        condition={
                          /* @ts-ignore */
                          values.businessRegistrationType === BusinessRegistrationType.RSA_ID
                        }
                      >
                        <Field
                          name="bankAccountHolderIdNumber"
                          type="tel"
                          mask={idMask}
                          placeholder="Account holder ID number"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          fieldType="masked"
                          guide={!inMiniApp}
                          className="dark"
                          automationid="Onboarding_Create_Account_Your_Details_Field_Bank_Account_Holder_Id_Number"
                        />
                      </ShowIf>

                      <ShowIf
                        condition={
                          /* @ts-ignore */
                          values.businessRegistrationType ===
                          BusinessRegistrationType.IDENTIFICATION_NUMBER
                        }
                      >
                        <Field
                          name="identificationType"
                          type="text"
                          placeholder="Select identification type"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInputVersionTwo}
                          fieldType="select"
                          isDark
                          items={IDENTIFICATION_TYPES}
                          getItemDisplayValue={(item: ListItem<IdentificationType>) => item.label}
                          onItemSelect={(item: ListItem<IdentificationType>) => item}
                          className="top-field"
                        />
                        {/* @ts-ignore */}
                        <ShowIf condition={!!values.identificationType}>
                          <Field
                            name="bankAccountHolderIdNumber"
                            type={
                              // @ts-ignore
                              values.identificationType?.value === IdentificationType.SA_ID
                                ? 'tel'
                                : 'text'
                            }
                            mask={getIdMaskBusiness(values)}
                            // @ts-ignore
                            placeholder={getIdPlaceholder(values.identificationType?.value)}
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInputVersionTwo}
                            fieldType="masked"
                            className="dark"
                            guide={
                              !inMiniApp &&
                              // @ts-ignore
                              values.identificationType?.value === IdentificationType.SA_ID
                            }
                            automationid="Revamp_Bank_Details_Account_Holder_ID_Number"
                          />
                        </ShowIf>
                      </ShowIf>
                    </ShowIf>
                  </ShowIf>

                  <FormLayout>
                    {[
                      <Field
                        key="bank"
                        name="bank"
                        type="text"
                        placeholder="Bank name"
                        items={bankInfo.bankList}
                        getItemDisplayValue={(item) => {
                          return item.label;
                        }}
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInputVersionTwo}
                        fieldType="select"
                        isDark={true}
                        automationIdPrefix="Onboarding_Create_Account_Your_Details_Field_Bank"
                        onItemSelect={(item) => {
                          TealiumEvents.bankingBankSelected(item.label, customerType);
                          if (isCreditVettingEnabled && item.allowUserToOverrideBranch) {
                            // prepopulate with universal branch name + code
                            setFieldValue('branchName', item.universalBranchName, false);
                            setFieldValue('branchCode', item.universalBranchCode, true);
                          }
                        }}
                      />,
                    ]}
                  </FormLayout>

                  <Field
                    name="bankAccountNumber"
                    maxLength={MAX_BANK_ACCOUNT_NUMBER_LENGTH}
                    type="tel"
                    fieldType="standard"
                    placeholder="Account number"
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    className="dark"
                    automationid="Onboarding_Create_Account_Your_Details_Field_Bank_Account_Number"
                  />
                  <Field
                    name="accountType"
                    type="text"
                    placeholder="Account type"
                    items={bankInfo.accountTypes}
                    getItemDisplayValue={(item) => item}
                    component={MiniAppFocusWrapper}
                    innerComponent={FloatingInput}
                    fieldType="select"
                    isDark={true}
                    automationIdPrefix="Onboarding_Create_Account_Your_Details_AccountType"
                    onItemSelect={(item) => TealiumEvents.bankingAccountType(item, customerType)}
                  />
                  {/* @ts-ignore */}
                  <ShowIf
                    condition={
                      isDebitOrderDateEnabled &&
                      values['rentOnlineDeviceOrdered'] &&
                      isCreditVettingEnabled
                    }
                  >
                    <Field
                      name="debitOrderDate"
                      type="text"
                      placeholder="Debit order date" // @ts-ignore
                      items={bankInfo.debitOrderValues}
                      getItemDisplayValue={(item) => item}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="select"
                      isDark={true}
                      automationIdPrefix="Onboarding_Create_Account_Your_Details_Debit_Order_Date"
                    />
                  </ShowIf>
                  <ShowIf
                    condition={!isCreditVettingEnabled || !!values?.bank?.allowUserToOverrideBranch}
                  >
                    <Field
                      name="branchName"
                      type="text"
                      placeholder="Branch name"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="dark"
                      automationid="Onboarding_Create_Account_Your_Details_Field_Branch_Name"
                    />
                    <Field
                      name="branchCode"
                      type="tel"
                      placeholder="Branch code"
                      maxLength={6}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="dark"
                      automationid="Onboarding_Create_Account_Your_Details_Field_Branch_Code"
                    />
                  </ShowIf>

                  <ShowIf condition={isCreditVettingEnabled}>
                    <FormLabel
                      label="Bank account open since"
                      className="bank-account-open-since-label"
                    >
                      <FormRowLayout>
                        {[
                          () => (
                            <Field
                              name="bankAccountOpenSinceMonth"
                              type="text"
                              placeholder="Month" // @ts-ignore
                              items={bankInfo.months.map((monthLabel, index) => {
                                return {
                                  index: index + 1,
                                  label: monthLabel,
                                };
                              })}
                              getItemDisplayValue={(item) => item.label}
                              component={MiniAppFocusWrapper}
                              innerComponent={FloatingInputVersionTwo}
                              fieldType="select"
                              isDark={true}
                              isComboField={true}
                              automationIdPrefix="Onboarding_Create_Account_Your_Details_Bank_Account_Open_Since_Month"
                            />
                          ),
                          () => (
                            <Field
                              name="bankAccountOpenSinceYear"
                              type="number"
                              placeholder="Year"
                              items={bankYearOpenedValues}
                              getItemDisplayValue={(item) => item}
                              maxLength={4}
                              component={MiniAppFocusWrapper}
                              innerComponent={FloatingInputVersionTwo}
                              fieldType="autocomplete"
                              isDark={true}
                              isComboField={true}
                              filterItemsWithInputValue={true}
                              className="dark bottom-field"
                              automationIdPrefix="Onboarding_Create_Account_Your_Details_Bank_Account_Open_Since_Month"
                            />
                          ),
                        ]}
                      </FormRowLayout>
                    </FormLabel>
                  </ShowIf>
                </Form>
              </>
            );
            return (
              <>
                <div className="your-details-container">
                  <div className="has-text-centered">
                    <h1
                      className={`page-title has-padding-bottom-4 has-mobile-padding-bottom-6 ${
                        merchant?.account?.sov_requested ? 'has-padding-top-4' : ''
                      }`}
                    >
                      Banking details
                    </h1>
                    {yourBankingDetails()}
                  </div>
                </div>
              </>
            );
          }}
        </Formik>
      </ShowIf>
      <div className="spacer" />
    </div>
  );

  function validateAccountOpenedYear(value) {
    let errorMessage =
      !value.bankAccountOpenSinceYear || value.bankAccountOpenSinceYear.length !== 4
        ? 'The year entered is invalid'
        : null;
    if (!errorMessage) {
      errorMessage =
        parseInt(value.bankAccountOpenSinceYear) > currentDate.year
          ? 'The year can not be in the future'
          : null;
    }

    return errorMessage;
  }

  function validateAccountOpenedMonth(value) {
    let errorMessage = !value.bankAccountOpenSinceMonth?.label
      ? 'The month entered is invalid'
      : null;
    if (!errorMessage && parseInt(value.bankAccountOpenSinceYear) === currentDate.year) {
      errorMessage =
        value.bankAccountOpenSinceMonth.index > currentDate.month
          ? 'The month can not be in the future'
          : null;
    }
    return errorMessage;
  }

  function validateFormCreditVettingEnabled(values) {
    const errors: { [key: string]: string } = {};

    const invalidBank = !values.bank; // @ts-ignore
    errors.bank = invalidBank ? 'The bank entered is invalid' : null;

    const invalidBankAccountNumber = !isValidBankAccountNumber(values.bankAccountNumber); // @ts-ignore
    errors.bankAccountNumber = invalidBankAccountNumber
      ? 'The bank account number entered is invalid'
      : null;

    const invalidAccountType = !bankInfo.accountTypes.includes(values.accountType); // @ts-ignore
    errors.accountType = invalidAccountType ? 'The account type entered is invalid' : null;

    if (isDebitOrderDateEnabled) {
      const invalidDebitOrderDate =
        values.rentOnlineDeviceOrdered &&
        !bankInfo.debitOrderValues.includes(values.debitOrderDate); // @ts-ignore
      errors.debitOrderDate = invalidDebitOrderDate
        ? 'The debit order date entered is invalid'
        : null;
    }

    // @ts-ignore
    errors.bankAccountOpenSinceMonth = validateAccountOpenedMonth(values);

    // @ts-ignore
    errors.bankAccountOpenSinceYear = validateAccountOpenedYear(values);

    switch (values.bankAccountHolderType) {
      case bankInfo.accountHolderType.PERSONAL:
        const bankAccountHolderTradeRootValidationError = validateBankAccountHolderNameTradeRoot(
          values.bankAccountHolder,
        );

        if (bankAccountHolderTradeRootValidationError) {
          // @ts-ignore
          errors.bankAccountHolder = bankAccountHolderTradeRootValidationError;
        } else {
          const invalidBankAccountHolder = !isValidAlphabetical(values.bankAccountHolder); // @ts-ignore
          errors.bankAccountHolder = invalidBankAccountHolder
            ? 'The account holder name entered is invalid'
            : null;
        }

        if (
          values.bankAccountHolderIdNumber &&
          (values.accountHolderIDType === IdentityType.RSA_ID ||
            values.accountHolderIDType === 'id') &&
          isValidID(stripWhitespace(values.bankAccountHolderIdNumber)) &&
          isUserUnderAgeIdNumber(stripWhitespace(values.bankAccountHolderIdNumber))
        ) {
          // @ts-ignore
          errors.bankAccountHolderIdNumber = 'The age limit is 18 years or older';
          setUserIsUnderAge(true);
        }

        if (
          !values.bankAccountHolderIdNumber ||
          ((values.accountHolderIDType === IdentityType.RSA_ID ||
            values.accountHolderIDType === 'id') &&
            !isValidID(stripWhitespace(values.bankAccountHolderIdNumber)))
        ) {
          // @ts-ignore
          errors.bankAccountHolderIdNumber = 'The bank account holder ID number entered is invalid';
        }

        if (!isCreditVettingEnabled || !!values?.bank?.allowUserToOverrideBranch) {
          const invalidBranchName = !isValidBranchName(values.branchName); // @ts-ignore
          errors.branchName = invalidBranchName ? 'The branch name entered is invalid' : null;

          const invalidBranchCode = !isValidBranchCode(values.branchCode); // @ts-ignore
          errors.branchCode = invalidBranchCode ? 'The branch code entered is invalid' : null;
        }
        break;

      case bankInfo.accountHolderType.BUSINESS:
        const businessNameTradeRootValidationError = validateBusinessNameTradeRoot(
          values.businessName,
        );

        if (businessNameTradeRootValidationError) {
          // @ts-ignore
          errors.businessName = businessNameTradeRootValidationError;
        } else {
          const invalidBusinessName = !isValidAlphabetical(values.businessName); // @ts-ignore
          errors.businessName = invalidBusinessName
            ? 'The account name may not contain special characters or numbers'
            : null;
        }

        switch (values.businessRegistrationType) {
          case BusinessRegistrationType.BUSINESS_REGISTRATION:
            const invalidBusinessRegistrationNumber =
              merchant?.account.business_type === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.trust
                ? !isValidRegistrationNumberTrust(values.businessRegistrationNumber)
                : !isValidRegistrationNumber(values.businessRegistrationNumber);

            // @ts-ignore
            errors.businessRegistrationNumber = invalidBusinessRegistrationNumber
              ? 'The business registration number entered is invalid'
              : null;
            break;

          case BusinessRegistrationType.RSA_ID:
            if (
              values.bankAccountHolderIdNumber &&
              (values.accountHolderIDType === IdentityType.RSA_ID ||
                values.accountHolderIDType === 'id') &&
              !isValidID(stripWhitespace(values.bankAccountHolderIdNumber)) &&
              isUserUnderAgeIdNumber(values.bankAccountHolderIdNumber)
            ) {
              // @ts-ignore
              errors.bankAccountHolderIdNumber = 'The age limit is 18 years or older';
              setUserIsUnderAge(true);
            }

            if (
              !values.bankAccountHolderIdNumber ||
              ((values.accountHolderIDType === IdentityType.RSA_ID ||
                values.accountHolderIDType === 'id') &&
                !isValidID(stripWhitespace(values.bankAccountHolderIdNumber)))
            ) {
              // @ts-ignore
              errors.bankAccountHolderIdNumber =
                'The bank account holder ID number entered is invalid';
            }

            break;
          case BusinessRegistrationType.IDENTIFICATION_NUMBER:
            if (!values.identificationType || !values.identificationType.value) {
              errors.identificationType = 'Please select a valid identification type';
            }

            if (
              (values.accountHolderIDType === IdentityType.RSA_ID ||
                values.identificationType?.value === IdentificationType.SA_ID ||
                values.identificationType?.value === 'id') &&
              !isValidID(stripWhitespace(values.bankAccountHolderIdNumber))
            ) {
              errors.bankAccountHolderIdNumber =
                'The bank account holder ID number entered is invalid';
            }

            const errorMessage = validateIdentificationType(
              values.bankAccountHolderIdNumber,
              values.identificationType?.value,
            );

            if (errorMessage) {
              errors.bankAccountHolderIdNumber = errorMessage;
            }

            break;
        }
    }

    return errors;
  }

  function validateFormCreditVettingDisabled(values) {
    const errors = {};

    const invalidBank = !values.bank; // @ts-ignore
    errors.bank = invalidBank ? 'The bank entered is invalid' : null;

    const invalidBankAccountNumber = !isValidBankAccountNumber(values.bankAccountNumber); // @ts-ignore
    errors.bankAccountNumber = invalidBankAccountNumber
      ? 'The bank account number entered is invalid'
      : null;

    const invalidAccountType = !bankInfo.accountTypes.includes(values.accountType); // @ts-ignore
    errors.accountType = invalidAccountType ? 'The account type entered is invalid' : null;

    const bankAccountHolderTradeRootValidationError = validateBankAccountHolderNameTradeRoot(
      values.bankAccountHolder,
    );

    const invalidBranchName = !isValidBranchName(values.branchName); // @ts-ignore
    errors.branchName = invalidBranchName ? 'The branch name entered is invalid' : null;

    const invalidBranchCode = !isValidBranchCode(values.branchCode); // @ts-ignore
    errors.branchCode = invalidBranchCode ? 'The branch code entered is invalid' : null;

    if (bankAccountHolderTradeRootValidationError) {
      // @ts-ignore
      errors.bankAccountHolder = bankAccountHolderTradeRootValidationError;
    } else {
      const invalidBankAccountHolder = !isValidAlphabetical(values.bankAccountHolder); // @ts-ignore
      errors.bankAccountHolder = invalidBankAccountHolder
        ? 'The account holder name entered is invalid'
        : null;
    }

    return errors;
  }

  function isFormHasErrors(errors) {
    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });
    return anyErrors;
  }

  function getFormErrors(values) {
    return isCreditVettingEnabled
      ? validateFormCreditVettingEnabled(values)
      : validateFormCreditVettingDisabled(values);
  }

  function validateForm(values) {
    const errors = getFormErrors(values);
    const anyErrors = isFormHasErrors(errors);
    const oldValues = getBankDetails();

    props.toggleNext(!anyErrors);

    if (!deepEqual(values, oldValues)) {
      if (!anyErrors) {
        if (isCreditVettingEnabled) {
          setBankingDetailsPassBav(false);
        }

        setBankDetails(values);
      }
    }
    return errors;
  }

  function validateIdentificationType(
    accountHolderIDNumber?: string,
    identificationType?: IdentificationType,
  ): string | undefined {
    // SA ID
    if (
      identificationType === IdentificationType.SA_ID &&
      (!accountHolderIDNumber || !isValidID(stripWhitespace(accountHolderIDNumber)))
    ) {
      return 'The ID number entered is invalid';
    }

    // SA ID underage
    if (
      identificationType === IdentificationType.SA_ID &&
      accountHolderIDNumber &&
      isValidID(stripWhitespace(accountHolderIDNumber)) &&
      isUserUnderAgeIdNumber(accountHolderIDNumber)
    ) {
      setUserIsUnderAge(true);
      return 'The age limit is 18 years or older ';
    }

    // Passport and Asylum seeker
    if (
      (identificationType === IdentificationType.PASSPORT ||
        identificationType === IdentificationType.ASYLUM_SEEKER) &&
      !accountHolderIDNumber
    ) {
      return 'Document ID is invalid';
    }

    // Refugee
    if (identificationType === IdentificationType.REFUGEE && !accountHolderIDNumber) {
      return 'Document ID is invalid';
    }
  }
}
